import { createSelector } from '@reduxjs/toolkit';
import { isUnknownBreachOptimizationsEnabled, isUseDedupedRecommendationsEnabled } from 'common/utilities/FlightSetting';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { parseISO } from 'date-fns';
import { countBy, filter, sortBy } from 'lodash';
import { BreachRecommendation, RecommendationId } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import { Breach, BreachEntity, BreachEntityGroup, BreachEntityRecommendation, BreachState } from './breach.types';
import { getITPFamilyMemberFromCsid } from './identityMonitoringSelectors';
import { IIdentityMonitoringState } from './identityMonitoringState';

/**
 * Extract the {@link BreachEntity} from a {@link BreachEntityGroup}
 *
 * @param group
 */
export const extractBreachEntitiesFromGroup = (group: BreachEntityGroup): BreachEntity[] => {
  let entities: (BreachEntity | undefined)[];

  // eslint-disable-next-line default-case
  switch (group.groupType) {
    case 'EbayCredentials':
    case 'PayPalCredentials':
      entities = [group.userId, group.password];
      break;
    case 'Name':
      entities = [group.firstName, group.middleName, group.lastName];
      break;
    case 'Phone':
      entities = [group.phoneNumber, group.extension];
      break;
    case 'MedicalId':
      entities = [group.id, group.provider];
      break;
    case 'CreditCard':
      entities = [
        group.cardNumber,
        group.cardType,
        group.cardExpYear,
        group.cardExpMonth,
        group.cardExpDay,
        group.expiryDate,
        group.cvn,
        group.pin,
        group.holderName,
        group.holderAddress,
      ];
      break;
    case 'BankAccount':
      entities = [group.accountNumber, group.routingNumber, group.pin, group.userId, group.password, group.bankName, group.bankPhone];
      break;
    case 'DriverLicense':
      entities = [group.number, group.state];
      break;
    case 'Passport':
      entities = [group.number, group.countryOrRegion, group.expiryDate, group.issueDate];
      break;
    case 'NationalId':
      entities = [group.id, group.countryOrRegion];
      break;
    case 'SSN':
    case 'IBAN':
    case 'Email':
    case 'CellPhone':
    case 'HomePhone':
    case 'WorkPhone':
    case 'RetailCard':
    case 'UserId':
    case 'Password':
    case 'MotherMaidenName':
    case 'DateOfBirth':
    case 'Address':
      entities = [group];
  }

  return entities.filter((entity): entity is BreachEntity => entity !== undefined);
};

/**
 * Extract the {@link BreachEntity} from a breach
 *
 * @param root0
 * @param root0.breachEntityGroups
 */
export const extractBreachEntities = ({ breachEntityGroups }: Breach): BreachEntity[] =>
  breachEntityGroups ? breachEntityGroups.flatMap(extractBreachEntitiesFromGroup) : [];

/**
 * Extract all the {@link BreachEntity} from a breach that have state of either 'New' or 'Remediated'
 *
 * @param breach
 */
export const extractNewOrRemediatedBreachEntities = (breach: Breach): BreachEntity[] =>
  extractBreachEntities(breach).filter((entity) => entity.entityState === 'New' || entity.entityState === 'Remediated');

/**
 * {@link Breach} with additional data derived by selectors
 */
export type AugmentedBreach = Breach & {
  /**
   * Whether the breach is actionable (i.e. has recommendations for the user to act upon)
   * If false, the breach will be part of the "Other data breaches" section and will have an "Archive" state when completed
   */
  isActionable: boolean;
  /**
   * Overall state of breach
   * If any entity is in state "New", the breach state is "Incomplete", otherwise it is "Complete"
   */
  state: BreachState;
  /**
   * A breach is marked as done if there is at least one "Remediated" entity, and no "New" entities
   */
  isMarkedDone: boolean;
  /**
   * A breach is marked as irrelevant if all entities are "FalsePositive" or "NotRelevant"
   */
  isBreachIrrelevant: boolean;
  /**
   * Whether the state of the breach is currently changing.
   * If any entity has a loading API status then the breach state is
   * loading.
   */
  isStateLoading: boolean;
  /**
   * Last time an entity was marked as "Remediated" or irrelevant ("FalsePositive" or "NotRelevant").
   * Only applies for completed breaches (incomplete breaches will return undefined).
   */
  breachCompletionDate: Date | undefined;
};

/**
 * Get the last modified / max date from a list of date,
 * or return undefined if the list is empty.
 *
 * @param entities
 * @returns {Date | undefined} Last modified date or undefined
 */
const getLastModifiedDate = (entities: BreachEntity[]) =>
  entities
    .map((entity) => entity.lastModifiedDate ?? new Date(0))
    .reduce((latest: Date | undefined, date) => (latest && latest > date ? latest : date), undefined);

/**
 * Gets the last time an entity was marked as "Remediated" (for breaches marked as done) or the last time
 * an entity was marked as irrelevant ("FalsePositive" or "NotRelevant") for a completed breach
 * @param state
 * @param isMarkedDone
 * @param entities
 * @returns {Date | undefined} Last modified date or undefined
 */
const getBreachCompletionDate = (state: BreachState, isMarkedDone: boolean, entities: BreachEntity[]) => {
  if (state === 'Complete') {
    return isMarkedDone
      ? getLastModifiedDate(entities.filter((entity) => entity.entityState === 'Remediated'))
      : getLastModifiedDate(
          entities.filter((entity) => entity.entityState === 'FalsePositive' || entity.entityState === 'NotRelevant')
        );
  }
  return undefined;
};

/**
 * Get recommendations that are associated with breach entities in 'New' or 'Remediated' state
 *
 * @param breach
 * @deprecated Use `breach.BreachRecommendations` instead
 */
const getActiveRecommendationsFromBreach = (breach: Breach | undefined): BreachEntityRecommendation[] => {
  if (!breach) {
    return [];
  }

  // filtering out recommendations associated with breach entities in resolved states
  const newBreachEntities = extractNewOrRemediatedBreachEntities(breach);
  const newBreachEntityIds = newBreachEntities.map((entity) => entity.entityId);
  return breach.recommendations.filter((r) => newBreachEntityIds.includes(r.breachEntityId));
};

/**
 * Augment a {@link Breach} with the {@link BreachState}, which is calculated based on whether
 * all of its {@link BreachEntity} have been resolved.
 *
 * @param breach
 */
export const augmentBreach = (breach: Breach): AugmentedBreach => {
  const entities = extractBreachEntities(breach);
  const state = entities.some((entity) => entity.entityState === 'New') ? 'Incomplete' : 'Complete';
  const isMarkedDone = state === 'Complete' && entities.some((entity) => entity.entityState === 'Remediated');

  return {
    ...breach,
    isActionable:
      !isUnknownBreachOptimizationsEnabled() ||
      (isUseDedupedRecommendationsEnabled()
        ? !!breach?.breachRecommendations.length
        : !!getActiveRecommendationsFromBreach(breach).length),
    state,
    isMarkedDone,
    isBreachIrrelevant: state === 'Complete' && !isMarkedDone,
    isStateLoading:
      breach.breachStateApiStatus === ApiCallResultStatus.LOADING ||
      entities.some((entity) => entity.entityStateApiStatus === ApiCallResultStatus.LOADING),
    breachCompletionDate: getBreachCompletionDate(state, isMarkedDone, entities),
  };
};

/**
 * Find a {@link Breach} from a list of {@link breaches} by {@link breachId}
 *
 * @param breaches
 * @param breachId
 */
export const findBreach = (breaches: Breach[] | undefined, breachId: string): Breach | undefined =>
  // we do not expect there to be many breaches, so a simple loop shall work fine
  breaches?.find((breach) => localeIgnoreCaseEquals(breach.breachId, breachId));

/**
 * Sorts given breaches by found date, from most to least recent
 * @param breaches
 * @returns {AugmentedBreach[]} Sorted breaches
 */
const sortBreachesByFoundDate = (breaches: AugmentedBreach[]) =>
  sortBy(breaches, [
    (b) => -(b.foundDate ? parseISO(b.foundDate).getTime() : Infinity), // later date comes first
  ]);

/**
 * Sorts completed breaches by completion date, from most to least recent
 * If completion date is the same, sorts in order of: marked as irrelevant, actionable, non-actionable, found date (most to least recent)
 * @param breaches
 * @returns {AugmentedBreach[]} Sorted breaches
 */
const sortCompletedBreaches = (breaches: AugmentedBreach[]) =>
  sortBy(breaches, [
    (b) => -(b.breachCompletionDate?.getTime() ?? Infinity), // later date comes first, based on breach completion date
    (b) => b.isMarkedDone, // isMarkedDone == false comes first
    (b) => !b.isActionable, // actionable breaches come first
    (b) => -(b.foundDate ? parseISO(b.foundDate).getTime() : Infinity), // later date comes first, based on breach detection date
  ]);

export const getBreaches: (
  state: IIdentityMonitoringState,
  csid: string | undefined
) => {
  allBreaches: AugmentedBreach[];
  normalBreaches: AugmentedBreach[];
  otherBreaches: AugmentedBreach[];
  completedBreaches: AugmentedBreach[];
  getBreachesApiStatus: ApiCallResultStatus;
} = createSelector([getITPFamilyMemberFromCsid], (member) => {
  const breaches = (member?.breaches || []).map(augmentBreach);
  return isUnknownBreachOptimizationsEnabled()
    ? {
        allBreaches: breaches, // sorted form is only used with normalBreaches, otherBreaches, and completedBreaches
        normalBreaches: sortBreachesByFoundDate(filter(breaches, (b) => b.state === 'Incomplete' && b.isActionable)),
        otherBreaches: sortBreachesByFoundDate(filter(breaches, (b) => b.state === 'Incomplete' && !b.isActionable)),
        completedBreaches: sortCompletedBreaches(filter(breaches, (b) => b.state === 'Complete')),
        getBreachesApiStatus: member?.getBreachesApiStatus || ApiCallResultStatus.DEFAULT,
      }
    : {
        allBreaches: sortBy(breaches, [
          (b) => b.state === 'Complete', // state === 'Incomplete' comes first
          (b) => b.isMarkedDone, // isMarkDone === false comes first
          (b) => -(b.foundDate ? parseISO(b.foundDate).getTime() : Infinity), // later date comes first
        ]),
        normalBreaches: [],
        otherBreaches: [],
        completedBreaches: [],
        getBreachesApiStatus: member?.getBreachesApiStatus || ApiCallResultStatus.DEFAULT,
      };
});

export const getBreachById: (state: IIdentityMonitoringState, breachId: string, csid?: string) => AugmentedBreach | undefined =
  createSelector(
    [
      (state: IIdentityMonitoringState) => state,
      // add second selector to consume breachId (see https://github.com/reduxjs/reselect#q-how-do-i-create-a-selector-that-takes-an-argument)
      (state: IIdentityMonitoringState, breachId: string) => breachId,
      (state: IIdentityMonitoringState, breachId: string, csid?: string) => csid,
    ],
    (state, breachId, csid) => {
      const member = getITPFamilyMemberFromCsid(state, csid);
      const found = findBreach(member?.breaches, breachId);
      return found && augmentBreach(found);
    }
  );

export const getBreachEntities: (state: IIdentityMonitoringState, breachId: string, csid?: string) => BreachEntity[] = createSelector(
  [
    (state: IIdentityMonitoringState) => state,
    (state: IIdentityMonitoringState, breachId: string) => breachId,
    (state: IIdentityMonitoringState, breachId: string, csid?: string) => csid,
  ],
  (state, breachId, csid) => {
    const member = getITPFamilyMemberFromCsid(state, csid);
    const breach = findBreach(member?.breaches, breachId);
    return breach ? extractBreachEntities(breach) : [];
  }
);

export const getBreachEntityById: (
  state: IIdentityMonitoringState,
  breachId: string,
  entityId: string,
  csid?: string
) => BreachEntity | undefined = createSelector(
  [
    (state: IIdentityMonitoringState) => state,
    (state: IIdentityMonitoringState, breachId: string) => breachId,
    (state: IIdentityMonitoringState, breachId: string, entityId: string) => entityId,
    (state: IIdentityMonitoringState, breachId: string, entityId: string, csid?: string) => csid,
  ],
  (state, breachId, entityId, csid) => {
    const entities = getBreachEntities(state, breachId, csid);
    return entities.find((entity) => localeIgnoreCaseEquals(entity.entityId, entityId)) ?? undefined;
  }
);

export const getBreachRecommendationById: (
  state: IIdentityMonitoringState,
  breachId: string,
  recommendationId: RecommendationId,
  csid?: string
) => BreachRecommendation | undefined = createSelector(
  [
    (state: IIdentityMonitoringState) => state,
    (state: IIdentityMonitoringState, breachId: string) => breachId,
    (state: IIdentityMonitoringState, breachId: string, recommendationId: string) => recommendationId,
    (state: IIdentityMonitoringState, breachId: string, recommendationId: string, csid?: string) => csid,
  ],
  (state, breachId, recommendationId, csid) => {
    const member = getITPFamilyMemberFromCsid(state, csid);
    return findBreach(member?.breaches, breachId)?.breachRecommendations.find((r) => r.recommendationId === recommendationId);
  }
);

/**
 * Get entity recommendation by entityId and recommendationId
 *
 * @param state
 * @param breachId
 * @param entityId
 * @param recommendationId
 * @param csid
 * @deprecated Use {@link getBreachRecommendationById} instead
 * @todo Remove this selector once {@link breach.BreachRecommendation} is fully adopted
 */
export const getEntityRecommendationById: (
  state: IIdentityMonitoringState,
  breachId: string,
  entityId: string,
  recommendationId: RecommendationId,
  csid?: string
) => BreachEntityRecommendation | undefined = createSelector(
  [
    (state: IIdentityMonitoringState) => state,
    (state: IIdentityMonitoringState, breachId: string) => breachId,
    (state: IIdentityMonitoringState, breachId: string, entityId: string) => entityId,
    (state: IIdentityMonitoringState, breachId: string, entityId: string, recommendationId: string) => recommendationId,
    (state: IIdentityMonitoringState, breachId: string, entityId: string, recommendationId: string, csid?: string) => csid,
  ],
  (state, breachId, entityId, recommendationId, csid) => {
    const member = getITPFamilyMemberFromCsid(state, csid);
    return findBreach(member?.breaches, breachId)?.recommendations.find(
      (r) => r.breachEntityId === entityId && r.recommendationId === recommendationId
    );
  }
);

export const getNewOrRemediatedBreachEntities: (state: IIdentityMonitoringState, breachId: string, csid?: string) => BreachEntity[] =
  createSelector(
    [
      (state: IIdentityMonitoringState) => state,
      (state: IIdentityMonitoringState, breachId: string) => breachId,
      (state: IIdentityMonitoringState, breachId: string, csid?: string) => csid,
    ],
    (state, breachId, csid) => {
      const member = getITPFamilyMemberFromCsid(state, csid);
      const breach = findBreach(member?.breaches, breachId);
      return breach ? extractNewOrRemediatedBreachEntities(breach) : [];
    }
  );

/**
 * Get recommendations that are associated with breach entities in 'New' or 'Remediated' state
 *
 * @param state
 * @param breachId
 * @param csid
 * @deprecated Use `breach.BreachRecommendations` instead
 */
export const getActiveRecommendations: (
  state: IIdentityMonitoringState,
  breachId: string,
  csid?: string
) => BreachEntityRecommendation[] = createSelector(
  [
    // input selectors
    (state: IIdentityMonitoringState) => state,
    (state: IIdentityMonitoringState, breachId: string) => breachId,
    (state: IIdentityMonitoringState, breachId: string, csid?: string) => csid,
  ],
  (state, breachId, csid) => {
    const member = getITPFamilyMemberFromCsid(state, csid);
    const breach = findBreach(member?.breaches, breachId);
    if (breach) {
      return getActiveRecommendationsFromBreach(breach);
    }

    return [];
  }
);

/**
 * Get counts of breaches per {@link BreachState}
 *
 * @param state
 * @param csid
 */
export const getBreachStateCounts: (state: IIdentityMonitoringState, csid?: string) => Record<BreachState, number | undefined> =
  createSelector(
    [(state: IIdentityMonitoringState) => state, (state: IIdentityMonitoringState, csid?: string) => csid],
    (state, csid) => {
      const member = getITPFamilyMemberFromCsid(state, csid);
      return countBy((member?.breaches || []).map(augmentBreach), (breach) => breach.state) as Record<BreachState, number | undefined>;
    }
  );

/**
 * Gets breaches for one-time scan (v1)
 *
 * @param state
 */
export const getOneTimeScanBreaches: (state: IIdentityMonitoringState) => {
  breaches: AugmentedBreach[];
  getOneTimeScanBreachesApiStatus: ApiCallResultStatus;
} = createSelector(
  [
    (state: IIdentityMonitoringState) => state.oneTimeScan?.breaches,
    (state: IIdentityMonitoringState) => state.oneTimeScan?.getExistingBreachesApiStatus,
  ],
  (breaches, getExistingBreachesApiStatus) => {
    return {
      breaches: sortBy((breaches || []).map(augmentBreach), [
        (b) => -(b.foundDate ? parseISO(b.foundDate).getTime() : Infinity), // later date comes first
      ]),
      getOneTimeScanBreachesApiStatus: getExistingBreachesApiStatus || ApiCallResultStatus.DEFAULT,
    };
  }
);

/**
 * Gets breaches for one-time scan (v2)
 *
 * @param state
 */
export const getUnauthOneTimeScanBreaches: (state: IIdentityMonitoringState) => {
  breaches: AugmentedBreach[];
} = createSelector([(state: IIdentityMonitoringState) => state.unauthOneTimeScan?.breaches], (breaches) => {
  return {
    breaches: sortBy(breaches || [], [
      (b) => -(b.foundDate ? parseISO(b.foundDate).getTime() : Infinity), // later date comes first
    ]),
  };
});
